import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { X, UserPlus, UserCheck, RefreshCw, Loader2, Users } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getMediaUrl } from '../utils/mediaUrl';
import VerifiedBadge from './VerifiedBadge';
import StoryViewer from './StoryViewer';

const T = {
  fr: { title: 'Suggestions', sub: 'Des membres actifs qui parlent votre langue', follow: "S'abonner", following: 'Abonné', refresh: 'Nouvelles suggestions', empty: 'Aucune suggestion pour le moment', posts: 'publications', followers: 'abonnés', burgerTitle: 'Ajouter des amis', burgerSub: 'Découvrez des membres actifs de votre langue' },
  en: { title: 'Suggestions', sub: 'Active members who speak your language', follow: 'Follow', following: 'Following', refresh: 'New suggestions', empty: 'No suggestions right now', posts: 'posts', followers: 'followers', burgerTitle: 'Add friends', burgerSub: 'Discover active members in your language' },
  es: { title: 'Sugerencias', sub: 'Miembros activos que hablan tu idioma', follow: 'Seguir', following: 'Siguiendo', refresh: 'Nuevas sugerencias', empty: 'Sin sugerencias por ahora', posts: 'publicaciones', followers: 'seguidores', burgerTitle: 'Añadir amigos', burgerSub: 'Descubre miembros activos en tu idioma' },
  ar: { title: 'اقتراحات', sub: 'أعضاء نشطون يتحدثون لغتك', follow: 'متابعة', following: 'تتابعه', refresh: 'اقتراحات جديدة', empty: 'لا توجد اقتراحات حاليًا', posts: 'منشورات', followers: 'متابعون', burgerTitle: 'إضافة أصدقاء', burgerSub: 'اكتشف أعضاء نشطين بلغتك' },
  pt: { title: 'Sugestões', sub: 'Membros ativos que falam o seu idioma', follow: 'Seguir', following: 'Seguindo', refresh: 'Novas sugestões', empty: 'Nenhuma sugestão no momento', posts: 'publicações', followers: 'seguidores', burgerTitle: 'Adicionar amigos', burgerSub: 'Descubra membros ativos no seu idioma' },
  de: { title: 'Vorschläge', sub: 'Aktive Mitglieder, die deine Sprache sprechen', follow: 'Folgen', following: 'Folge ich', refresh: 'Neue Vorschläge', empty: 'Derzeit keine Vorschläge', posts: 'Beiträge', followers: 'Follower', burgerTitle: 'Freunde hinzufügen', burgerSub: 'Entdecke aktive Mitglieder in deiner Sprache' },
  ru: { title: 'Рекомендации', sub: 'Активные участники, говорящие на вашем языке', follow: 'Подписаться', following: 'Вы подписаны', refresh: 'Новые рекомендации', empty: 'Пока нет рекомендаций', posts: 'публикаций', followers: 'подписчиков', burgerTitle: 'Добавить друзей', burgerSub: 'Найдите активных участников на вашем языке' },
  zh: { title: '推荐', sub: '说您语言的活跃成员', follow: '关注', following: '已关注', refresh: '换一批', empty: '暂无推荐', posts: '帖子', followers: '粉丝', burgerTitle: '添加好友', burgerSub: '发现使用您语言的活跃成员' },
  ja: { title: 'おすすめ', sub: 'あなたの言語を話すアクティブなメンバー', follow: 'フォロー', following: 'フォロー中', refresh: '他のおすすめ', empty: '現在おすすめはありません', posts: '投稿', followers: 'フォロワー', burgerTitle: '友達を追加', burgerSub: 'あなたの言語のアクティブなメンバーを見つける' },
  hi: { title: 'सुझाव', sub: 'आपकी भाषा बोलने वाले सक्रिय सदस्य', follow: 'फ़ॉलो करें', following: 'फ़ॉलो कर रहे हैं', refresh: 'नए सुझाव', empty: 'अभी कोई सुझाव नहीं', posts: 'पोस्ट', followers: 'फ़ॉलोअर', burgerTitle: 'दोस्त जोड़ें', burgerSub: 'अपनी भाषा के सक्रिय सदस्यों को खोजें' },
  nl: { title: 'Suggesties', sub: 'Actieve leden die jouw taal spreken', follow: 'Volgen', following: 'Volgend', refresh: 'Nieuwe suggesties', empty: 'Nu geen suggesties', posts: 'berichten', followers: 'volgers', burgerTitle: 'Vrienden toevoegen', burgerSub: 'Ontdek actieve leden in jouw taal' },
  sw: { title: 'Mapendekezo', sub: 'Wanachama hai wanaozungumza lugha yako', follow: 'Fuata', following: 'Unafuata', refresh: 'Mapendekezo mapya', empty: 'Hakuna mapendekezo kwa sasa', posts: 'machapisho', followers: 'wafuasi', burgerTitle: 'Ongeza marafiki', burgerSub: 'Gundua wanachama hai wa lugha yako' },
  ht: { title: 'Sijesyon', sub: 'Manm aktif ki pale lang ou', follow: 'Abòne', following: 'Abòne', refresh: 'Nouvo sijesyon', empty: 'Pa gen sijesyon kounye a', posts: 'piblikasyon', followers: 'abòne', burgerTitle: 'Ajoute zanmi', burgerSub: 'Dekouvri manm aktif nan lang ou' },
};
export const suggestionTexts = (lang) => T[lang] || T.en;

const Avatar = ({ u, onOpenStory, onOpenProfile }) => (
  <button
    onClick={() => (u.has_active_story ? onOpenStory(u.id) : onOpenProfile(u.id))}
    className={`relative w-14 h-14 rounded-full p-[2px] shrink-0 ${u.has_active_story ? 'bg-gradient-to-tr from-yellow-400 via-fuchsia-500 to-purple-600' : 'bg-white/10'}`}
    data-testid={`suggestion-avatar-${u.id}`}
    aria-label={u.name}
  >
    <div className="w-full h-full rounded-full overflow-hidden bg-gray-900 border-2 border-gray-900 flex items-center justify-center">
      {u.profile_image_url ? (
        <img src={getMediaUrl(u.profile_image_url)} alt={u.name} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <span className="text-lg font-bold text-white">{(u.name || u.username || '?').charAt(0).toUpperCase()}</span>
      )}
    </div>
  </button>
);

export const UserSuggestionsModal = ({ onClose }) => {
  const { language } = useLanguage();
  const S = suggestionTexts(language);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storyUser, setStoryUser] = useState(null);
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/people/suggestions', { params: { limit: 20 } });
      setItems(r.data.items || []);
    } catch (e) { setItems([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const follow = async (u) => {
    if (busy[u.id]) return;
    setBusy((b) => ({ ...b, [u.id]: true }));
    try {
      const r = await axios.post(`/api/users/${u.id}/follow`);
      setItems((prev) => prev.map((x) => (x.id === u.id ? { ...x, is_following: !!r.data.following } : x)));
    } catch (e) { toast.error('Erreur'); } finally { setBusy((b) => ({ ...b, [u.id]: false })); }
  };

  const openProfile = (id) => { onClose?.(); navigate(`/profile/${id}`); };

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center" onClick={onClose} data-testid="user-suggestions-modal">
      <div className="w-full max-w-md bg-gray-900 border border-white/10 rounded-t-3xl sm:rounded-3xl max-h-[88vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-white/10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-yellow-400 to-fuchsia-600 flex items-center justify-center"><Users className="w-5 h-5 text-white" /></div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-white">{S.title}</h3>
            <p className="text-xs text-gray-400 truncate">{S.sub}</p>
          </div>
          <button onClick={load} className="p-2 rounded-full hover:bg-white/10 text-gray-300" title={S.refresh} data-testid="suggestions-refresh"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10" data-testid="suggestions-close"><X className="w-5 h-5 text-white" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="suggestions-list">
          {loading && items.length === 0 ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-fuchsia-400 animate-spin" /></div>
          ) : items.length === 0 ? (
            <p className="text-center text-gray-500 text-sm py-12">{S.empty}</p>
          ) : items.map((u) => (
            <div key={u.id} className="flex items-center gap-3 p-2.5 rounded-2xl bg-white/5 border border-white/5" data-testid={`suggestion-${u.id}`}>
              <Avatar u={u} onOpenStory={setStoryUser} onOpenProfile={openProfile} />
              <button onClick={() => openProfile(u.id)} className="flex-1 min-w-0 text-left">
                <p className="font-semibold text-white truncate flex items-center gap-1">{u.name || u.username}{u.is_verified && <VerifiedBadge size={14} />}</p>
                <p className="text-xs text-gray-400 truncate">@{u.username} · {u.recent_posts} {S.posts} · {u.followers_count} {S.followers}</p>
                {u.bio && <p className="text-[11px] text-gray-500 truncate">{u.bio}</p>}
              </button>
              <button
                onClick={() => follow(u)}
                disabled={busy[u.id]}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1 transition-colors ${u.is_following ? 'bg-white/10 text-gray-300' : 'bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white hover:opacity-90'}`}
                data-testid={`suggestion-follow-${u.id}`}
              >
                {u.is_following ? <UserCheck className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}
                {u.is_following ? S.following : S.follow}
              </button>
            </div>
          ))}
        </div>
      </div>
      {storyUser && <div className="fixed inset-0 z-[10001]" data-testid="suggestions-story-viewer"><StoryViewer userId={storyUser} onClose={() => setStoryUser(null)} /></div>}
    </div>,
    document.body
  );
};

export default UserSuggestionsModal;
